import React from 'react';
import {
  Outlet,
  Route,
  createBrowserRouter,
  createRoutesFromElements,
  RouterProvider,
} from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';

const Login = React.lazy(() => import('./contexts/pages/Login'));
const Home = React.lazy(() => import('./contexts/pages/Home'));
const Services = React.lazy(() => import('./contexts/pages/Services'));
const Contact = React.lazy(() => import('./contexts/pages/Contact'));
const Dashboard = React.lazy(() => import('./contexts/pages/Dashboard'));
const Events = React.lazy(() => import('./contexts/pages/Events'));
const AdminPanel = React.lazy(() => import('./contexts/pages/AdminPanel'));
const Statistics = React.lazy(() => import('./contexts/pages/Statistics'));
const Profile = React.lazy(() => import('./contexts/pages/Profile'));
const NotFound = React.lazy(() => import('./contexts/pages/NotFound'));
const ProtectedRoute = React.lazy(() => import('./lib/components/ProtectedRoute'));

const AppRouteLoader = () => (
  <div className="flex min-h-screen items-center justify-center px-6">
    <div className="flex items-center gap-3 rounded-full border border-zinc-800 bg-zinc-950/90 px-4 py-2 text-sm text-zinc-300 shadow-lg shadow-black/20">
      <span className="h-2.5 w-2.5 animate-pulse rounded-full bg-violet-400" />
      Laddar
    </div>
  </div>
);

const RouteBoundary: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <React.Suspense fallback={<AppRouteLoader />}>{children}</React.Suspense>
);

const ProtectedOutlet: React.FC = () => (
  <AuthProvider>
    <ProtectedRoute>
      <Outlet />
    </ProtectedRoute>
  </AuthProvider>
);

const AdminOutlet: React.FC = () => (
  <AuthProvider>
    <ProtectedRoute requireAdmin>
      <Outlet />
    </ProtectedRoute>
  </AuthProvider>
);

const router = createBrowserRouter(
  createRoutesFromElements(
    <Route>
      <Route path="/login" element={<RouteBoundary><Login /></RouteBoundary>} />
      <Route path="/" element={<RouteBoundary><Home /></RouteBoundary>} />
      <Route path="/services" element={<RouteBoundary><Services /></RouteBoundary>} />
      <Route path="/contact" element={<RouteBoundary><Contact /></RouteBoundary>} />

      <Route element={<RouteBoundary><ProtectedOutlet /></RouteBoundary>}>
        <Route path="/dashboard" element={<RouteBoundary><Dashboard /></RouteBoundary>} />
        <Route path="/events" element={<RouteBoundary><Events /></RouteBoundary>} />
        <Route path="/profile" element={<RouteBoundary><Profile /></RouteBoundary>} />
      </Route>

      <Route element={<RouteBoundary><AdminOutlet /></RouteBoundary>}>
        <Route path="/admin" element={<RouteBoundary><AdminPanel /></RouteBoundary>} />
        <Route path="/statistics" element={<RouteBoundary><Statistics /></RouteBoundary>} />
      </Route>

      <Route path="*" element={<RouteBoundary><NotFound /></RouteBoundary>} />
    </Route>
  )
);

const App = () => {
  return (
    <RouteBoundary>
      <RouterProvider router={router} />
    </RouteBoundary>
  );
};

export default App;
